import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../core/auth.service';
import { ApiClientService, RegistrationSource } from '../../core/api-client.service';
import { SiteConfigService } from '../../core/site-config.service';
import {
  ProductQuickDraft,
  ProductQuickEditorData,
  ProductQuickEditorService,
  ProductQuickTemplate,
} from '../product-quick-editor/product-quick-editor.service';
import {
  SellerOnboardingPayload,
  SellerOnboardingService,
  SellerOnboardingStepKey,
} from '../seller-onboarding/seller-onboarding.service';
import {
  SupportCategoryPayload,
  SupportService,
} from '../support/support.service';
import { SellerPanelProfile, SellerPanelService } from '../seller-panel/seller-panel.service';

export type RegisterFieldKey = 'name' | 'email' | 'password' | 'policies';
export type SellerQuickProductFieldKey =
  | 'templateID'
  | 'categoryID'
  | 'title'
  | 'description'
  | 'price'
  | 'credit'
  | 'currency'
  | 'durationMinutes'
  | 'listingID'
  | 'active'
  | 'imageUploads'
  | 'quickOptions';

export interface RegisterPayloadContract {
  name: string;
  email: string;
  password: string;
  policies: Record<string, boolean>;
}

/**
 * Conversation UI adapter for existing forms. It deliberately owns no API
 * endpoint and no alternative field names: final writes go through the same
 * services used by the canonical registration and quick-product forms.
 */
@Injectable({ providedIn: 'root' })
export class GuidedActionService {
  readonly registerFieldKeys: readonly RegisterFieldKey[] = ['name', 'email', 'password', 'policies'];
  readonly sellerQuickProductFieldKeys: readonly SellerQuickProductFieldKey[] = [
    'templateID', 'categoryID', 'title', 'description', 'price', 'credit', 'currency',
    'durationMinutes', 'listingID', 'active', 'imageUploads', 'quickOptions',
  ];

  readonly auth = inject(AuthService);
  readonly site = inject(SiteConfigService);
  private readonly products = inject(ProductQuickEditorService);
  private readonly onboarding = inject(SellerOnboardingService);
  private readonly support = inject(SupportService);
  private readonly sellerPanel = inject(SellerPanelService);
  private readonly api = inject(ApiClientService);

  async prepareRegistration(): Promise<void> {
    await this.auth.loadRegisterPolicies();
  }

  async login(email: string, password: string): Promise<boolean> {
    return this.auth.login(email, password);
  }

  async register(payload: RegisterPayloadContract, source: RegistrationSource = 'guided_action'): Promise<boolean> {
    this.assertExactKeys(payload as unknown as Record<string, unknown>, this.registerFieldKeys, 'Kayıt');
    for (const policy of this.auth.registerPolicies()) {
      this.auth.setRegisterPolicyConsent(policy.id, payload.policies[String(policy.id)] === true);
    }
    return this.auth.register(payload.name, payload.email, payload.password, source);
  }

  async productData(): Promise<ProductQuickEditorData> {
    return this.products.data();
  }

  draftFromTemplate(data: ProductQuickEditorData, template: ProductQuickTemplate): ProductQuickDraft {
    const product = template.sellerProduct;
    return {
      templateID: template.id,
      categoryID: template.categoryID,
      listingID: product?.listingID ?? null,
      title: product?.title || template.title,
      description: product?.description || template.description,
      price: data.pricingMode === 'credit' ? null : (product?.price || template.defaultPrice),
      credit: data.pricingMode === 'credit' ? (product?.credit || template.defaultCredit || 0) : null,
      currency: product?.currency || template.currency || data.currency,
      durationMinutes: template.durationMinutes || 1440,
      active: product?.active ?? true,
      imageUploads: product?.imageUploads ? structuredClone(product.imageUploads) : [],
      quickOptions: structuredClone(template.quickOptions ?? []),
    };
  }

  async uploadProductImage(file: File): Promise<Record<string, unknown>> {
    return this.products.uploadImage(file);
  }

  async saveSellerProduct(draft: ProductQuickDraft): Promise<Record<string, unknown>> {
    this.assertExactKeys(draft as unknown as Record<string, unknown>, this.sellerQuickProductFieldKeys, 'Ürün');
    return this.products.save('listing', draft);
  }

  async sellerOnboarding(): Promise<SellerOnboardingPayload> {
    return this.onboarding.get();
  }

  async saveSellerOnboardingStep(
    stepKey: SellerOnboardingStepKey,
    nextStep: SellerOnboardingStepKey,
    formData: Record<string, unknown>,
  ): Promise<SellerOnboardingPayload> {
    return this.onboarding.saveStep(stepKey, nextStep, formData);
  }

  async checkSellerShopName(shopName: string): Promise<{ result: boolean; message?: string; shopName?: string }> {
    return this.onboarding.checkShopName(shopName);
  }

  async completeSellerOnboarding(): Promise<SellerOnboardingPayload> {
    return this.onboarding.complete();
  }

  async uploadSellerProfileImage(file: File): Promise<Record<string, unknown>> {
    return this.sellerPanel.uploadSellerProfileAsset('avatar', file);
  }

  async saveSellerProfileImage(upload: Record<string, unknown>): Promise<SellerPanelProfile> {
    const profile = await this.sellerPanel.sellerProfile();
    return this.sellerPanel.saveSellerProfile({
      shopName: String(profile.shopName || this.auth.user()?.seller?.shopName || '').trim(),
      profilePictureUploads: [upload],
    });
  }

  async completeSellerPostStep(stepKey: 'profileImage' | 'firstProduct'): Promise<SellerOnboardingPayload> {
    return this.onboarding.savePostStep(stepKey);
  }

  async supportCenter(): Promise<SupportCategoryPayload> {
    return this.support.center();
  }

  async supportCategory(slug: string): Promise<SupportCategoryPayload> {
    return this.support.category(slug);
  }

  async supportMethod(ticketMethodID: number) {
    return this.support.method(ticketMethodID);
  }

  async createSupportTicket(methodID: number, values: Record<string, unknown>, categoryID: number): Promise<void> {
    return this.support.createTicket(methodID, values, categoryID);
  }

  async qrOperation(operationKey: string, payload: Record<string, unknown> = {}): Promise<Record<string, any>> {
    const response = await firstValueFrom(this.api.post<any>('/api/app/operation', { operationKey, payload }));
    if (response?.success === false || response?.result === false) throw new Error(response?.message || 'QR menü işlemi tamamlanamadı.');
    return response?.data ?? {};
  }

  async analyzeImageForm(file: File, schemaKey: string, instruction = ''): Promise<Record<string, any>> {
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) throw new Error('JPEG, PNG veya WebP fotoğraf seçin.');
    const imageDataUri = await this.optimizedImageDataUri(file);
    return this.qrOperation('ai.image-form.analyze', { schemaKey, imageDataUri, instruction });
  }

  async applyQrPhotoMenu(branchID: number, formData: Record<string, unknown>): Promise<Record<string, any>> {
    return this.qrOperation('restaurant.menu.photo.apply', { branchID, formData });
  }

  async uploadQrMenuImage(file: File): Promise<Record<string, unknown>> {
    const body = new FormData();
    body.append('snpImageUpload', file);
    body.append('lastModified', String(file.lastModified || Date.now()));
    const response = await firstValueFrom(this.api.postFormData<any>('/api/app/product-image-upload', body));
    if (response?.success === false || response?.result === false) throw new Error(response?.message || 'Görsel yüklenemedi.');
    return response?.data?.mediaUpload ?? {};
  }

  private assertExactKeys(payload: Record<string, unknown>, allowed: readonly string[], label: string): void {
    const unexpected = Object.keys(payload).filter((key) => !allowed.includes(key));
    if (unexpected.length) throw new Error(`${label} formu mevcut sözleşmede olmayan alan içeriyor: ${unexpected.join(', ')}`);
    const missing = allowed.filter((key) => !Object.prototype.hasOwnProperty.call(payload, key));
    if (missing.length) throw new Error(`${label} formunda mevcut sözleşmenin zorunlu alanları eksik: ${missing.join(', ')}`);
  }

  private optimizedImageDataUri(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const objectUrl = URL.createObjectURL(file);
      image.onload = () => {
        try {
          const max = 2200;
          const scale = Math.min(1, max / Math.max(image.naturalWidth, image.naturalHeight));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
          canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
          const context = canvas.getContext('2d');
          if (!context) throw new Error('Fotoğraf işlenemedi.');
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
          const dataUri = canvas.toDataURL('image/jpeg', 0.9);
          if (dataUri.length > 11_200_000) throw new Error('Fotoğraf çok büyük. Daha düşük çözünürlüklü bir fotoğraf seçin.');
          resolve(dataUri);
        } catch (error) { reject(error); }
        finally { URL.revokeObjectURL(objectUrl); }
      };
      image.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Fotoğraf okunamadı.')); };
      image.src = objectUrl;
    });
  }
}
